import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { presetService } from '../../services/presetService';
import { tradeSetService } from '../../services/tradeSetService';
import { orderService } from '../../services/orderService';
import { Preset, TradeSet } from '../../types/preset';
import { toast } from 'react-toastify';

interface TradeSetBarProps {
  selectedSymbol: string;
  onExecutePreset?: (preset: Preset) => Promise<void>;
  onExecuteTradeSet?: (tradeSet: TradeSet) => Promise<void>;
}

const BarContainer = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing(2.5)};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  background-color: ${({ theme }) => theme.colors.background.paper}E6; /* 90% opacity */
  border-radius: ${({ theme }) => theme.shape.borderRadius.large};
  padding: ${({ theme }) => theme.spacing(1.5)};
  box-shadow: ${({ theme }) => theme.shadows.large};
  z-index: 20; /* Keep it above chart content, but below trade panel */
  max-width: 90%;
  overflow-x: auto;
  gap: ${({ theme }) => theme.spacing(1.5)};
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  min-height: 60px;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.shape.borderRadius.small};
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.primary.main};
  }
`;

const ItemButton = styled.button<{ itemType: 'preset' | 'tradeset' | 'system' }>`
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => theme.shape.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: ${({ theme }) => theme.typography.button.fontWeight};
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  border: none;
  transition: all 0.2s ease;
  color: ${({ theme }) => theme.colors.text.contrast};
  background-color: ${({ theme, itemType }) => {
    switch(itemType) {
      case 'preset': return theme.colors.primary.main;
      case 'tradeset': return theme.colors.secondary.main;
      case 'system': return '#dc2626'; // Red for system/danger actions
      default: return theme.colors.primary.main;
    }
  }};
  box-shadow: ${({ theme }) => theme.shadows.small};
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ItemIcon = styled.span<{ itemType: 'preset' | 'tradeset' | 'system' }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: ${({ itemType }) => {
    switch(itemType) {
      case 'preset': return '3px';
      case 'tradeset': return '50%';
      case 'system': return '2px';
      default: return '3px';
    }
  }};
  background-color: #fff;
`;

const PinButton = styled.button<{ isPinned: boolean }>`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: 50%;
  background-color: ${({ isPinned }) => isPinned ? '#22c55e' : '#6b7280'};
  color: white;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  opacity: 0;
  transition: all 0.2s ease;
  
  ${ItemButton}:hover & {
    opacity: 1;
  }
  
  &:hover {
    background-color: ${({ isPinned }) => isPinned ? '#16a34a' : '#4b5563'};
    transform: scale(1.1);
  }
`;

const SectionDivider = styled.div`
  width: 1px;
  height: 30px;
  background-color: ${({ theme }) => theme.colors.border};
  opacity: 0.5;
`;

const LoadingPlaceholder = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: 500;
  text-align: center;
`;

const ManagePinButton = styled.button`
  padding: ${({ theme }) => theme.spacing(0.5)} ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.shape.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const ExpansionContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1)};
  margin-left: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.shape.borderRadius.medium};
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  margin-left: 6px;
  border-radius: 8px;
  font-size: 10px;
  background-color: rgba(0,0,0,0.25);
  color: #fff;
`;

// System default actions
const SYSTEM_ACTIONS = [
  {
    id: 'cancel_all',
    name: 'Cancel All',
    description: 'Cancel all open orders',
    icon: '✕'
  },
  {
    id: 'cancel_bids',
    name: 'Cancel Bids',
    description: 'Cancel all buy orders',
    icon: '↓'
  },
  {
    id: 'cancel_asks',
    name: 'Cancel Asks', 
    description: 'Cancel all sell orders',
    icon: '↑'
  },
  {
    id: 'best_bid',
    name: 'Best Bid',
    description: 'Place order at best bid price',
    icon: '⇣'
  },
  {
    id: 'best_ask',
    name: 'Best Ask',
    description: 'Place order at best ask price', 
    icon: '⇡'
  }
];

// Helper functions for pinning persistence
const PINNED_ITEMS_KEY = 'tradeset-bar-pinned-items';

interface PinnedItems {
  presets: string[];
  tradeSets: string[];
}

const loadPinnedItems = (): PinnedItems => {
  try {
    const stored = localStorage.getItem(PINNED_ITEMS_KEY);
    return stored ? JSON.parse(stored) : { presets: [], tradeSets: [] };
  } catch (error) {
    console.error('Error loading pinned items:', error);
    return { presets: [], tradeSets: [] };
  }
};

const savePinnedItems = (pinnedItems: PinnedItems) => {
  try {
    localStorage.setItem(PINNED_ITEMS_KEY, JSON.stringify(pinnedItems));
  } catch (error) {
    console.error('Error saving pinned items:', error);
  }
};

const TradeSetBar: React.FC<TradeSetBarProps> = ({ 
  selectedSymbol,
  onExecutePreset,
  onExecuteTradeSet
}) => {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [tradeSets, setTradeSets] = useState<TradeSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState<string | null>(null);
  const [pinnedItems, setPinnedItems] = useState<PinnedItems>(loadPinnedItems());
  const [showAllItems, setShowAllItems] = useState(false);
  const [expandedTradeSetId, setExpandedTradeSetId] = useState<string | null>(null);
  const lastClickTs = useRef<number>(0);

  // Fetch presets and tradeSets for the selected symbol
  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const [presetsResponse, tradeSetsResponse] = await Promise.all([
          presetService.getPresets(1, 20, undefined, selectedSymbol),
          tradeSetService.getTradeSets(1, 20, selectedSymbol)
        ]);
        setPresets(presetsResponse.items);
        setTradeSets(tradeSetsResponse.items);
      } catch (error) {
        console.error('Error fetching items for TradeSetBar:', error);
        toast.error('Failed to load quick trading items');
      } finally {
        setLoading(false);
      }
    };
    
    if (selectedSymbol) {
      fetchItems();
    }
  }, [selectedSymbol]);

  // Handle pin/unpin
  const handleTogglePin = (itemId: string, itemType: 'preset' | 'tradeset') => {
    const newPinnedItems = { ...pinnedItems };
    
    if (itemType === 'preset') {
      const isCurrentlyPinned = pinnedItems.presets.includes(itemId);
      if (isCurrentlyPinned) {
        newPinnedItems.presets = pinnedItems.presets.filter(id => id !== itemId);
        toast.info('Preset unpinned from bar');
      } else {
        newPinnedItems.presets = [...pinnedItems.presets, itemId];
        toast.success('Preset pinned to bar');
      }
    } else {
      const isCurrentlyPinned = pinnedItems.tradeSets.includes(itemId);
      if (isCurrentlyPinned) {
        newPinnedItems.tradeSets = pinnedItems.tradeSets.filter(id => id !== itemId);
        toast.info('TradeSet unpinned from bar');
      } else {
        newPinnedItems.tradeSets = [...pinnedItems.tradeSets, itemId];
        toast.success('TradeSet pinned to bar');
      }
    }
    
    setPinnedItems(newPinnedItems);
    savePinnedItems(newPinnedItems);
  };

  // Filter items based on pinning and display mode
  const getDisplayItems = () => {
    if (showAllItems) {
      return { presets, tradeSets };
    }
    
    // Show only pinned items by default
    const pinnedPresets = presets.filter(preset => 
      pinnedItems.presets.includes(preset.id)
    );
    const pinnedTradeSets = tradeSets.filter(tradeSet => 
      pinnedItems.tradeSets.includes(tradeSet.id)
    );
    
    return { 
      presets: pinnedPresets, 
      tradeSets: pinnedTradeSets 
    };
  };

  // Handle executing a preset
  const handlePresetClick = async (preset: Preset) => {
    const now = Date.now();
    if (now - lastClickTs.current < 400) return; // Debounce rapid clicks
    lastClickTs.current = now;
    if (executing) return; // Prevent multiple executions
    
    setExecuting(preset.id.toString());
    try {
      if (onExecutePreset) {
        await onExecutePreset(preset);
      } else {
        // Default implementation if no handler provided
        const result = await presetService.executePreset(preset.id.toString());
        toast.success(`Preset '${preset.name}' executed`);
      }
    } catch (error: any) {
      console.error('Error executing preset:', error);
      toast.error(`Failed to execute preset: ${error.message || 'Unknown error'}`);
    } finally {
      setExecuting(null);
    }
  };

  // Handle clicking a tradeSet (expand inline to show linked presets)
  const handleTradeSetClick = async (tradeSet: TradeSet) => {
    // Toggle expansion inline
    setExpandedTradeSetId(prev => (prev === tradeSet.id ? null : tradeSet.id));
  };

  // Handle system action execution
  const handleSystemAction = async (actionId: string) => {
    if (executing) return; // Prevent multiple executions
    
    setExecuting(actionId);
    try {
      let orderRequest: any = {
        symbol: selectedSymbol
      };

      switch (actionId) {
        case 'cancel_all':
          orderRequest = {
            ...orderRequest,
            order_type: 'cancel_all'
          };
          break;
        case 'cancel_bids':
          orderRequest = {
            ...orderRequest,
            order_type: 'cancel_bids'
          };
          break;
        case 'cancel_asks':
          orderRequest = {
            ...orderRequest,
            order_type: 'cancel_asks'
          };
          break;
        case 'best_bid':
          orderRequest = {
            ...orderRequest,
            order_type: 'limit',
            side: 'buy',
            amount: 0.001, // Default small amount
            amount_type: 'base',
            limit_price_type: 'best_bid',
            flags: {
              post_only: true,
              reduce_only: false
            }
          };
          break;
        case 'best_ask':
          orderRequest = {
            ...orderRequest,
            order_type: 'limit',
            side: 'sell',
            amount: 0.001, // Default small amount
            amount_type: 'base',
            limit_price_type: 'best_ask',
            flags: {
              post_only: true,
              reduce_only: false
            }
          };
          break;
        default:
          toast.error(`Unknown system action: ${actionId}`);
          return;
      }

      // Execute the order via the order service
      const result = await orderService.placeOrder(orderRequest);
      
      if (result.success) {
        const action = SYSTEM_ACTIONS.find(a => a.id === actionId);
        toast.success(`${action?.name || 'Action'} executed successfully`);
      } else {
        toast.error(`Failed to execute action: ${result.message || 'Unknown error'}`);
      }

    } catch (error: any) {
      console.error('Error executing system action:', error);
      toast.error(`Failed to execute action: ${error.message || 'Unknown error'}`);
    } finally {
      setExecuting(null);
    }
  };

  // Show placeholder during loading
  if (loading) {
    return (
      <BarContainer>
        <LoadingPlaceholder>Loading trading shortcuts...</LoadingPlaceholder>
      </BarContainer>
    );
  }

  const { presets: displayPresets, tradeSets: displayTradeSets } = getDisplayItems();
  const hasUserItems = displayPresets.length > 0 || displayTradeSets.length > 0;

  return (
    <BarContainer>
      {/* System Default Buttons - Always show these first */}
      {SYSTEM_ACTIONS.map(action => (
        <ItemButton
          key={`system-${action.id}`}
          itemType="system"
          onClick={() => handleSystemAction(action.id)}
          disabled={!!executing}
          title={action.description}
        >
          <ItemIcon itemType="system">{action.icon}</ItemIcon>
          {action.name}
        </ItemButton>
      ))}
      
      {/* Divider if we have user presets/tradeSets */}
      {hasUserItems && <SectionDivider />}
      
      {/* User Preset buttons */}
      {displayPresets.map(preset => (
        <ItemButton
          key={`preset-${preset.id}`}
          itemType="preset"
          onClick={() => handlePresetClick(preset)}
          disabled={!!executing}
          title={`Execute preset: ${preset.name}`}
        >
          <ItemIcon itemType="preset" />
          {preset.name}
          <PinButton
            isPinned={pinnedItems.presets.includes(preset.id)}
            onClick={(e) => {
              e.stopPropagation();
              handleTogglePin(preset.id, 'preset');
            }}
            title={pinnedItems.presets.includes(preset.id) ? 'Unpin from bar' : 'Pin to bar'}
          >
            📌
          </PinButton>
        </ItemButton>
      ))}
      
      {/* User TradeSet buttons with inline expansion */}
      {displayTradeSets.map(tradeSet => (
        <React.Fragment key={`tradeset-${tradeSet.id}`}>
          <ItemButton
            itemType="tradeset"
            onClick={() => handleTradeSetClick(tradeSet)}
            disabled={!!executing}
            title={`Open tradeSet: ${tradeSet.name}`}
          >
            <ItemIcon itemType="tradeset" />
            {tradeSet.name}
            <CountBadge>{Array.isArray((tradeSet as any).presets) ? (tradeSet as any).presets.length : 0}</CountBadge>
            <PinButton
              isPinned={pinnedItems.tradeSets.includes(tradeSet.id)}
              onClick={(e) => {
                e.stopPropagation();
                handleTogglePin(tradeSet.id, 'tradeset');
              }}
              title={pinnedItems.tradeSets.includes(tradeSet.id) ? 'Unpin from bar' : 'Pin to bar'}
            >
              📌
            </PinButton>
          </ItemButton>
          {expandedTradeSetId === tradeSet.id && Array.isArray((tradeSet as any).presets) && (
            <ExpansionContainer>
              {(tradeSet as any).presets.map((p: any) => (
                <ItemButton
                  key={`ts-${tradeSet.id}-preset-${p.id}`}
                  itemType="preset"
                  onClick={() => handlePresetClick({ id: p.id, name: p.name } as Preset)}
                  disabled={!!executing}
                  title={`Execute preset: ${p.name}`}
                >
                  <ItemIcon itemType="preset" />
                  {p.name}
                </ItemButton>
              ))}
            </ExpansionContainer>
          )}
        </React.Fragment>
      ))}
      
      {/* Manage pins button */}
      {(presets.length > 0 || tradeSets.length > 0) && (
        <>
          <SectionDivider />
          <ManagePinButton
            onClick={() => setShowAllItems(!showAllItems)}
            title={showAllItems ? 'Show only pinned items' : 'Show all items for pinning'}
          >
            {showAllItems ? '📌 Pinned' : '⚙️ Manage'}
          </ManagePinButton>
        </>
      )}
      
      {/* Show placeholder only if no user items exist */}
      {presets.length === 0 && tradeSets.length === 0 && (
        <LoadingPlaceholder>
          No user presets or tradeSets for {selectedSymbol}.
          Create some to see them here!
        </LoadingPlaceholder>
      )}
    </BarContainer>
  );
};

export default TradeSetBar; 